// FILE: src/components/app/bases/BudgetBaseComponent/hooks/usePlannedTransactions.js
import * as React from "react";
import { subscribeUserTransactions } from "../../../../../utils/firebase/features/transactions/fetchers";

/** helpers */
const toISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};
const normTag = (s) => String(s || "").trim().toLowerCase();
const isFutureOrToday = (dateStr, todayISO) => {
  // tolerate Timestamp/Date/ISO
  if (!dateStr) return false;
  try {
    if (typeof dateStr?.toDate === "function") dateStr = dateStr.toDate();
    const iso = typeof dateStr === "string" ? dateStr : toISO(new Date(dateStr));
    return iso >= todayISO; // simple string compare works for yyyy-MM-dd
  } catch {
    try {
      const d = new Date(dateStr);
      const t0 = new Date(todayISO);
      d.setHours(0,0,0,0);
      t0.setHours(0,0,0,0);
      return d.getTime() >= t0.getTime();
    } catch { return false; }
  }
};

/**
 * Live planned-transactions view for a user:
 *  - Subscribes to all (recent) user transactions (cap configurable)
 *  - Filters client-side to future-dated rows
 *  - Indexes 2 ways: by budgetId and by "budget / <slug>" tag
 *  - Provides `getPlannedFor(budget)` = union(byId, byTag) de-duped by tx.id
 *
 * This avoids cache staleness after creating/deleting planned tx.
 */
export default function usePlannedTransactions({
  userEmail,
  cap = 5000,
  horizonDays = 365, // kept for API compat; we just need "future", not the exact horizon
}) {
  const [byId, setById] = React.useState(new Map());
  const [byTag, setByTag] = React.useState(new Map());
  const snapshotRef = React.useRef([]); // last full snapshot for manual refresh()

  const refreshPlanned = React.useCallback(() => {
    // recompute groupings from the last snapshot (useful if parent state like budgetTagPath changes)
    const rows = Array.isArray(snapshotRef.current) ? snapshotRef.current : [];
    const todayISO = toISO(new Date());
    const mId = new Map();
    const mTag = new Map();

    for (const r of rows) {
      if (!isFutureOrToday(r?.date, todayISO)) continue;

      // group by budgetId
      if (r?.budgetId) {
        const k = String(r.budgetId);
        if (!mId.has(k)) mId.set(k, []);
        mId.get(k).push(r);
      }

      // group by budget tag (either tagPath or tagPaths)
      const tags =
        Array.isArray(r?.tagPath) ? r.tagPath :
        Array.isArray(r?.tagPaths) ? r.tagPaths :
        [];
      for (const t of tags) {
        const tl = normTag(t);
        if (!tl.startsWith("budget /")) continue; // only index budget tags
        if (!mTag.has(tl)) mTag.set(tl, []);
        mTag.get(tl).push(r);
      }
    }

    setById(mId);
    setByTag(mTag);
  }, []);

  React.useEffect(() => {
    if (!userEmail) {
      setById(new Map());
      setByTag(new Map());
      snapshotRef.current = [];
      return;
    }

    // subscribe to ALL user transactions (new & updates)
    const unsub = subscribeUserTransactions({
      userEmail,
      cap,
      onData: (docs) => {
        snapshotRef.current = Array.isArray(docs) ? docs : [];
        refreshPlanned();
      },
      onError: () => {
        setById(new Map());
        setByTag(new Map());
        snapshotRef.current = [];
      },
    });

    return () => { try { unsub && unsub(); } catch {} };
  }, [userEmail, cap, refreshPlanned]);

  const getPlannedFor = React.useCallback((budget) => {
    if (!budget) return [];
    const arr = [];
    const seen = new Set();

    // via direct id
    const a = byId.get(String(budget.id)) || [];
    for (const tx of a) {
      const id = String(tx.id || "");
      if (!seen.has(id)) { seen.add(id); arr.push(tx); }
    }

    // via budget tag
    const tagKey = normTag(budget?.budgetTagPath?.[0]);
    if (tagKey) {
      const b = byTag.get(tagKey) || [];
      for (const tx of b) {
        const id = String(tx.id || "");
        if (!seen.has(id)) { seen.add(id); arr.push(tx); }
      }
    }

    return arr;
  }, [byId, byTag]);

  return { plannedByBudgetId: byId, plannedByBudgetTag: byTag, getPlannedFor, refreshPlanned };
}
